import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, CalendarDays, Sparkles, MessageSquareQuote, MailQuestion, BookOpen, LogOut, Menu, X } from "lucide-react";
import { api } from "../../lib/api";

const NAV = [
  { to: "/admin", icon: LayoutDashboard, label: "Tableau de bord", end: true },
  { to: "/admin/bookings", icon: BookOpen, label: "Réservations" },
  { to: "/admin/calendar", icon: CalendarDays, label: "Calendrier" },
  { to: "/admin/events", icon: Sparkles, label: "Picnic Electronic" },
  { to: "/admin/testimonials", icon: MessageSquareQuote, label: "Témoignages" },
  { to: "/admin/inquiries", icon: MailQuestion, label: "Demandes" },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [checked, setChecked] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    api.get("/auth/me")
      .then(() => setChecked(true))
      .catch(() => navigate("/admin/login", { replace: true }));
  }, [navigate]);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const logout = () => {
    localStorage.removeItem("tnp-admin-token");
    navigate("/admin/login");
  };

  if (!checked) return <div className="min-h-screen bg-cream flex items-center justify-center text-earth">Chargement…</div>;

  const navContent = (
    <>
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
            className={({ isActive }) => `flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm transition-colors ${isActive ? "bg-terracotta text-ivory" : "text-ivory/60 hover:text-ivory hover:bg-ivory/5"}`}>
            <n.icon size={17} /> {n.label}
          </NavLink>
        ))}
      </nav>
      <button data-testid="admin-logout-btn" onClick={logout}
        className="m-4 flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm text-ivory/60 hover:text-ivory hover:bg-ivory/5 transition-colors">
        <LogOut size={17} /> Déconnexion
      </button>
    </>
  );

  return (
    <div data-testid="admin-layout" className="min-h-screen bg-cream lg:flex">
      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-40 bg-ink text-ivory flex items-center justify-between px-4 h-14 shrink-0">
        <img src="/assets/brand/logo.jpg" alt="The Nice Picnic" className="h-8 w-auto rounded-md" />
        <button data-testid="admin-menu-btn" onClick={() => setMenuOpen(true)} aria-label="Ouvrir le menu"
          className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-ivory/10 transition-colors">
          <Menu size={22} />
        </button>
      </div>

      {/* Mobile drawer backdrop */}
      {menuOpen && (
        <div data-testid="admin-menu-backdrop" className="fixed inset-0 z-40 bg-ink/50 lg:hidden" onClick={() => setMenuOpen(false)} />
      )}

      {/* Sidebar: drawer on mobile, static on desktop */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 max-w-[85vw] bg-ink text-ivory flex flex-col transform transition-transform duration-300 lg:static lg:z-auto lg:translate-x-0 lg:sticky lg:top-0 lg:h-screen lg:w-64 lg:shrink-0 ${menuOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="p-6 border-b border-ivory/10 flex items-start justify-between">
          <div>
            <img src="/assets/brand/logo.jpg" alt="The Nice Picnic" className="h-10 w-auto rounded-lg" />
            <span className="block text-[0.55rem] tracking-[0.4em] uppercase text-gold mt-2">Back-office</span>
          </div>
          <button data-testid="admin-menu-close" onClick={() => setMenuOpen(false)} aria-label="Fermer le menu"
            className="lg:hidden w-9 h-9 flex items-center justify-center rounded-lg text-ivory/60 hover:text-ivory hover:bg-ivory/10 transition-colors">
            <X size={18} />
          </button>
        </div>
        {navContent}
      </aside>

      <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-10 overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
}
